import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@hooks/useDebounce';

import {
  IntakeVerificationQueue,
  VERIFICATION_FILTERS,
} from './components/IntakeVerificationQueue';
import { useGetAdminIntakeVerificationQueueQuery } from './store/api';

export function AdminIntakeVerificationQueue(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const search = (params.get('verificationSearch') ?? '').slice(0, 200);
  const debouncedSearch = useDebounce(search, 300);
  const filter =
    VERIFICATION_FILTERS.find(option => option.value === params.get('verificationStatus'))?.value ??
    'awaiting';
  const requestedPage = Number(params.get('verificationPage') ?? 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const requestedSize = Number(params.get('verificationPerPage') ?? 20);
  const perPage = [10, 20, 50].includes(requestedSize) ? requestedSize : 20;
  const { currentData, isFetching, isError, refetch } = useGetAdminIntakeVerificationQueueQuery(
    { search: debouncedSearch, state: filter, page, perPage },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true },
  );
  const update = (values: Record<string, string>): void => {
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        Object.entries(values).forEach(([key, value]) =>
          next.set('verification' + key[0].toUpperCase() + key.slice(1), value),
        );
        return next;
      },
      { replace: true },
    );
  };
  useEffect(() => {
    if (
      currentData &&
      !isFetching &&
      !isError &&
      page > Math.max(1, currentData.total_pages ?? 0)
    ) {
      setParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set('verificationPage', String(Math.max(1, currentData.total_pages ?? 0)));
          return next;
        },
        { replace: true },
      );
    }
  }, [currentData, isFetching, isError, page, setParams]);
  return (
    <IntakeVerificationQueue
      returnTo={'/admin-v2/intake-status?' + params.toString()}
      data={search === debouncedSearch ? currentData : undefined}
      loading={isFetching || search !== debouncedSearch}
      failed={isError}
      search={search}
      filter={filter}
      page={page}
      perPage={perPage}
      onSearch={value => update({ search: value, page: '1' })}
      onFilter={value => update({ status: value, page: '1' })}
      onPage={value => update({ page: String(value) })}
      onPerPage={value => update({ perPage: String(value), page: '1' })}
      onRefresh={() => {
        void refetch();
      }}
    />
  );
}
